import { Modal } from "@truevault/aquilo";
import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { EditModeBlockedModal } from "./EditModeBlockedModal";
import {
  clearEditModeFlag,
  getEditModeDurationLabel,
  getNewEditModeExpiresAt,
  getRemainingEditModeMs,
  getStoredEditModeFlag,
  setEditModeFlag,
  storeEditModeFlag,
} from "./editModeState";

type EditModeContextValue = {
  isEditMode: boolean;
  requestEditMode: () => void;
  setIsEditMode: React.Dispatch<React.SetStateAction<boolean>>;
};

const EditModeContext = React.createContext<EditModeContextValue | undefined>(undefined);

export const EditModeProvider = ({ children }: { children: React.ReactNode }) => {
  const [isEditMode, setIsEditMode] = useState(getStoredEditModeFlag);
  const [showEditModeConfirmation, setShowEditModeConfirmation] = useState(false);

  useEffect(() => {
    setEditModeFlag(isEditMode);

    if (!isEditMode) {
      storeEditModeFlag(false);
    }
  }, [isEditMode]);

  useEffect(() => {
    if (!isEditMode) {
      return;
    }

    const remainingEditModeMs = getRemainingEditModeMs();
    if (remainingEditModeMs <= 0) {
      clearEditModeFlag();
      setIsEditMode(false);
      return;
    }

    const timeout = window.setTimeout(() => {
      clearEditModeFlag();
      setIsEditMode(false);
    }, remainingEditModeMs);

    return () => window.clearTimeout(timeout);
  }, [isEditMode]);

  const requestEditMode = useCallback(() => {
    if (isEditMode) {
      return;
    }

    setShowEditModeConfirmation(true);
  }, [isEditMode]);

  const closeEditModeConfirmation = () => setShowEditModeConfirmation(false);

  const confirmEditMode = () => {
    storeEditModeFlag(true, getNewEditModeExpiresAt());
    setIsEditMode(true);
    closeEditModeConfirmation();
  };

  const value = useMemo(
    () => ({
      isEditMode,
      requestEditMode,
      setIsEditMode,
    }),
    [isEditMode, requestEditMode],
  );

  return (
    <EditModeContext.Provider value={value}>
      {children}
      <EditModeBlockedModal onSwitchToEditMode={requestEditMode} />
      {showEditModeConfirmation && (
        <Modal
          title="Switch to edit mode?"
          body={`You will be able to make changes as a global admin for ${getEditModeDurationLabel()}. Continue only when you're ready to edit.`}
          firstButton="Switch to edit mode"
          firstButtonColor="primary"
          secondButton="Cancel"
          secondButtonColor="tertiary"
          showOverlay={true}
          closeOnOverlayClick={true}
          onFirstButtonClick={confirmEditMode}
          onSecondButtonClick={closeEditModeConfirmation}
          onClose={closeEditModeConfirmation}
        />
      )}
    </EditModeContext.Provider>
  );
};

export const useEditMode = () => {
  const context = useContext(EditModeContext);

  if (!context) {
    throw new Error("useEditMode must be used within an EditModeProvider.");
  }

  return context;
};
